import React, { useCallback, useContext, useEffect, useState } from "react";
import { DrawModeChangeEvent } from "@mapbox/mapbox-gl-draw";
import { Box, ToggleButtonGroup } from "@mui/material";

import { MapContext } from "../MapProvider";

import { ToolbarGroup } from "./tools";

import { Paper } from "./toolbar.styles";

export interface IButtonProps {
  title?: string;
  icon?: React.ReactNode;
  mode?: string;
}

const Toolbar: React.FC = () => {
  const { map, drawControl, isDrawing, toolbar } = useContext(MapContext);
  const [mode, setMode] = useState<string | null>(null);

  useEffect(() => {
    if (isDrawing && map?.hasControl(drawControl.current)) {
      setMode(drawControl.current.getMode());
    }
  }, [isDrawing, drawControl, map]);

  /**
   * Handles the changing of the current drawmode
   */
  const handleModeChange = useCallback(
    (event: DrawModeChangeEvent) => {
      setMode(event.mode);
    },
    [setMode]
  );

  useEffect(() => {
    map?.on("draw.modechange", handleModeChange);
    return () => {
      map?.off("draw.modechange", handleModeChange);
    };
  }, [map, handleModeChange]);

  if (!isDrawing) return null;

  const renderButtonGroup = (group: ToolbarGroup) => {
    const { items, id } = group;
    return (
      <Paper elevation={3} key={id}>
        <ToggleButtonGroup exclusive orientation="vertical" size="small" value={mode}>
          {items.map((item) => React.cloneElement(item, { mode }))}
        </ToggleButtonGroup>
      </Paper>
    );
  };

  const renderActionGroup = (group: ToolbarGroup) => {
    const { items, id } = group;
    return (
      <Paper elevation={3} key={id}>
        {items.map((item) => React.cloneElement(item, { mode }))}
      </Paper>
    );
  };

  return (
    <Box sx={{ position: "absolute", top: 5, left: 5, display: "flex", flexDirection: "column", zIndex: 999 }}>
      {toolbar.current?.map((config) => {
        if (config.type === "mode") return renderButtonGroup(config);
        return renderActionGroup(config);
      })}
    </Box>
  );
};

export default Toolbar;
